import React, {
    ReactElement,
    useEffect,
    useState,
} from 'react'
import { Navigate } from 'react-router-dom'

import { useAppSelector } from '../../hooks/redux'
import OverlayLoader from '../../components/shared/overlay-loader'
import { logout } from '../../components/shared/auth'

interface GuardProps {
    children: ReactElement
}

/**
 * Route guard that requires an authenticated, approved user.
 *
 * Redirects to /login if the user is missing, shows a loader while
 * auth is in flight, or renders a denied message if the user is not
 * approved yet.
 */
export const RequireAuth = ({ children }: GuardProps): ReactElement => {
    const authedUser = useAppSelector(state => state.auth.user) as { approved?: boolean } | null
    const busyAuth = useAppSelector(state => state.auth.busy)

    if (busyAuth) {
        return <OverlayLoader />
    }

    if (!authedUser) {
        return <Navigate to="/login" replace />
    }

    if (!authedUser.approved) {
        return <div>Nemáte zde přístup!</div>
    }

    return children
}

/**
 * Route guard that requires an authenticated super admin user.
 */
export const RequireSuperAdmin = ({ children }: GuardProps): ReactElement => {
    const authedUser = useAppSelector(state => state.auth.user) as { superAdmin?: boolean } | null
    const busyAuth = useAppSelector(state => state.auth.busy)

    if (busyAuth) {
        return <OverlayLoader />
    }

    if (!authedUser) {
        return <Navigate to="/login" replace />
    }

    if (!authedUser.superAdmin) {
        return <div>Nemáte zde přístup!</div>
    }

    return children
}

/**
 * Route guard that logs the current user out before rendering its
 * children. Used for the /logout route.
 */
export const RequireLogout = ({ children }: GuardProps): ReactElement | null => {
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
        logout()
        setIsReady(true)
    }, [])

    if (!isReady) {
        return null
    }

    return children
}
